/**
 * WASM kernel for the NSDF hot loop (J1). The O(n·maxLag) autocorrelation is
 * the only expensive part of pitch detection, so exactly that loop is compiled
 * to WebAssembly; peak-picking stays in JS (detectPitchFromNsdf) where it is
 * shared with the pure-JS fallback path.
 *
 * The module is emitted as raw bytecode at runtime — no build toolchain, no
 * .wasm asset to ship, and the same bytes run in Node (vitest verifies the
 * kernel against computeNsdf) and inside an AudioWorklet (passed through
 * processorOptions and instantiated on the audio thread).
 *
 * Memory layout: input Float32Array[n] at byte 0, nsdf Float32Array[maxLag+1]
 * at byte n*4. Exported: memory "mem", function "nsdf(n, minLag, maxLag)".
 */
import { detectPitchFromNsdf, nsdfLagRange, rmsLevel, DEFAULT_CLARITY_THRESHOLD, DEFAULT_RMS_GATE } from './pitch.js';
import { freqToMidi } from './music.js';
// ── bytecode emission ───────────────────────────────────────────────────────
function uleb(n) {
    const out = [];
    do {
        let byte = n & 0x7f;
        n >>>= 7;
        if (n !== 0)
            byte |= 0x80;
        out.push(byte);
    } while (n !== 0);
    return out;
}
function f64bytes(v) {
    const b = new Uint8Array(8);
    new DataView(b.buffer).setFloat64(0, v, true);
    return [...b];
}
function section(id, content) {
    return [id, ...uleb(content.length), ...content];
}
function str(s) {
    const b = [...new TextEncoder().encode(s)];
    return [...uleb(b.length), ...b];
}
// local indices: params n=0, minLag=1, maxLag=2; locals tau=3, i=4, limit=5,
// outBase=6 (i32), acf=7, m=8, a=9, b=10 (f64)
const GET = 0x20;
const SET = 0x21;
/** Emit the complete WASM module bytes. Pure function of nothing — cacheable. */
export function nsdfWasmBytes() {
    const body = [
        // locals: 4×i32, 4×f64
        ...uleb(2), ...uleb(4), 0x7f, ...uleb(4), 0x7c,
        // outBase = n << 2
        GET, 0, 0x41, 2, 0x74, SET, 6,
        // tau = minLag
        GET, 1, SET, 3,
        0x02, 0x40, // block $exit
        0x03, 0x40, // loop $tau
        GET, 3, GET, 2, 0x4a, 0x0d, 1, // if tau > maxLag br $exit
        // acf = 0; m = 0; i = 0; limit = n - tau
        0x44, ...f64bytes(0), SET, 7,
        0x44, ...f64bytes(0), SET, 8,
        0x41, 0, SET, 4,
        GET, 0, GET, 3, 0x6b, SET, 5,
        0x02, 0x40, // block $idone
        0x03, 0x40, // loop $iloop
        GET, 4, GET, 5, 0x4e, 0x0d, 1, // if i >= limit br $idone
        // a = f64(buf[i])
        GET, 4, 0x41, 2, 0x74, 0x2a, 2, 0, 0xbb, SET, 9,
        // b = f64(buf[i + tau])
        GET, 4, GET, 3, 0x6a, 0x41, 2, 0x74, 0x2a, 2, 0, 0xbb, SET, 10,
        // acf += a * b
        GET, 7, GET, 9, GET, 10, 0xa2, 0xa0, SET, 7,
        // m += a*a + b*b
        GET, 8, GET, 9, GET, 9, 0xa2, GET, 10, GET, 10, 0xa2, 0xa0, 0xa0, SET, 8,
        // i++
        GET, 4, 0x41, 1, 0x6a, SET, 4,
        0x0c, 0, // br $iloop
        0x0b, 0x0b, // end loop, end block
        // nsdf[tau]: addr = outBase + (tau << 2)
        GET, 6, GET, 3, 0x41, 2, 0x74, 0x6a,
        // value = m > 0 ? 2*acf/m : 0
        GET, 8, 0x44, ...f64bytes(0), 0x64,
        0x04, 0x7c, // if (result f64)
        0x44, ...f64bytes(2), GET, 7, 0xa2, GET, 8, 0xa3,
        0x05, // else
        0x44, ...f64bytes(0),
        0x0b, // end if
        0xb6, // f32.demote_f64
        0x38, 2, 0, // f32.store align=2
        // tau++
        GET, 3, 0x41, 1, 0x6a, SET, 3,
        0x0c, 0, // br $tau
        0x0b, 0x0b, // end loop, end block
        0x0b, // end function
    ];
    const bytes = [
        0x00, 0x61, 0x73, 0x6d, 0x01, 0x00, 0x00, 0x00, // \0asm v1
        // type: (i32,i32,i32) -> ()
        ...section(1, [...uleb(1), 0x60, ...uleb(3), 0x7f, 0x7f, 0x7f, ...uleb(0)]),
        // function: [type 0]
        ...section(3, [...uleb(1), ...uleb(0)]),
        // memory: min 1 page (64KB — a 2048-sample window + nsdf fits comfortably)
        ...section(5, [...uleb(1), 0x00, ...uleb(1)]),
        // exports: mem, nsdf
        ...section(7, [...uleb(2), ...str('mem'), 0x02, ...uleb(0), ...str('nsdf'), 0x00, ...uleb(0)]),
        // code
        ...section(10, [...uleb(1), ...uleb(body.length), ...body]),
    ];
    return new Uint8Array(bytes);
}
/** Instantiate the WASM kernel. Mirrors the JS detectPitch API exactly. */
export async function createNsdfKernel() {
    const { instance } = await WebAssembly.instantiate(nsdfWasmBytes().buffer);
    const exports = instance.exports;
    function run(buf, sampleRate) {
        const n = buf.length;
        const { minLag, maxLag } = nsdfLagRange(n, sampleRate);
        const needed = (n + maxLag + 1) * 4;
        if (exports.mem.buffer.byteLength < needed) {
            exports.mem.grow(Math.ceil((needed - exports.mem.buffer.byteLength) / 65536));
        }
        new Float32Array(exports.mem.buffer, 0, n).set(buf);
        exports.nsdf(n, minLag, maxLag);
        // copy out — the view would be detached by a later grow
        return { nsdf: new Float32Array(exports.mem.buffer.slice(n * 4, needed)), minLag, maxLag };
    }
    return {
        computeNsdf(buf, sampleRate) {
            return run(buf, sampleRate).nsdf;
        },
        detectPitch(buf, sampleRate, opts = {}) {
            const rms = rmsLevel(buf);
            if (rms < (opts.rmsGate ?? DEFAULT_RMS_GATE))
                return null;
            const { nsdf, minLag, maxLag } = run(buf, sampleRate);
            const res = detectPitchFromNsdf(nsdf, minLag, maxLag, sampleRate, opts.clarityThreshold ?? DEFAULT_CLARITY_THRESHOLD);
            if (!res)
                return null;
            return { freqHz: res.freqHz, midi: freqToMidi(res.freqHz), clarity: res.clarity, rms };
        },
    };
}
//# sourceMappingURL=nsdf-wasm.js.map